import React from 'react';
import { ExternalLink, CheckCircle, Music, Play, Users, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const SpotifyIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

const YouTubeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const OfficialPresence = () => {
  const { t } = useLanguage();
  const op = (key) => t(`officialPresence.${key}`);
  const badges = op('badges');
  const spotifyFeatures = op('spotifyFeatures');
  const youtubeFeatures = op('youtubeFeatures');

  return (
    <section className="section-padding relative overflow-hidden" data-testid="official-presence-section">
      <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-[#1DB954]/5 blur-[150px] rounded-full pointer-events-none -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-red-500/5 blur-[150px] rounded-full pointer-events-none -translate-y-1/2" />

      <div className="container-custom relative">
        <div className="text-center mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E8720C] mb-4">
            {op('overline')}
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            {op('title1')}{' '}
            <span className="gold-text">{op('title2')}</span>
          </h2>
          <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed">
            {op('description')}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-6 mb-12">
          {[Music, Users, Play, Globe].map((Icon, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-white/50">
              <Icon className="w-4 h-4 text-[#E8720C]" />
              {Array.isArray(badges) ? badges[i] : ''}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Spotify Card */}
          <div className="glass-card p-8 relative overflow-hidden group hover:-translate-y-1 transition-all duration-300" data-testid="official-spotify-card">
            <div className="absolute inset-0 bg-gradient-to-br from-[#1DB954]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-[#1DB954]/10 flex items-center justify-center">
                  <SpotifyIcon className="w-9 h-9 text-[#1DB954]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">OG CENTER</h3>
                  <p className="text-sm text-white/40">{op('spotifySubtitle')}</p>
                </div>
              </div>
              <p className="text-sm text-white/60 mb-4 leading-relaxed">{op('spotifyDesc')}</p>
              <ul className="space-y-2 mb-6">
                {(Array.isArray(spotifyFeatures) ? spotifyFeatures : []).map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-white/50">
                    <CheckCircle className="w-3.5 h-3.5 text-[#1DB954]" />
                    {item}
                  </li>
                ))}
              </ul>
              <a href="https://open.spotify.com/user/31gofgummrn24fopehxrjzonqxoq?si=cb6f44425c74434a" target="_blank" rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-3 py-4 rounded-full bg-[#1DB954] hover:bg-[#1ed760] text-black font-bold text-sm transition-all hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(29,185,84,0.3)]"
                data-testid="official-spotify-btn">
                <SpotifyIcon className="w-5 h-5" />
                {op('spotifyBtn')}
                <ExternalLink className="w-4 h-4 opacity-60" />
              </a>
            </div>
          </div>

          {/* YouTube Card */}
          <div className="glass-card p-8 relative overflow-hidden group hover:-translate-y-1 transition-all duration-300" data-testid="official-youtube-card">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center">
                  <YouTubeIcon className="w-9 h-9 text-red-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">OG MUSIC GROUP</h3>
                  <p className="text-sm text-white/40">{op('youtubeSubtitle')}</p>
                </div>
              </div>
              <p className="text-sm text-white/60 mb-4 leading-relaxed">{op('youtubeDesc')}</p>
              <ul className="space-y-2 mb-6">
                {(Array.isArray(youtubeFeatures) ? youtubeFeatures : []).map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-white/50">
                    <CheckCircle className="w-3.5 h-3.5 text-red-500" />
                    {item}
                  </li>
                ))}
              </ul>
              <a href="https://www.youtube.com/@OGMUSICGROUP" target="_blank" rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-3 py-4 rounded-full bg-red-600 hover:bg-red-500 text-white font-bold text-sm transition-all hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(239,68,68,0.3)]"
                data-testid="official-youtube-btn">
                <YouTubeIcon className="w-5 h-5" />
                {op('youtubeBtn')}
                <ExternalLink className="w-4 h-4 opacity-60" />
              </a>
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-white/40 max-w-xl mx-auto">{op('trustMessage')}</p>
        </div>
      </div>
    </section>
  );
};

export default OfficialPresence;
